namespace Solovis.Reports.TableFilters {
    
    export interface TagFilterControlOptions extends Solovis.UI.BaseControlOptions {
        initialState: Solovis.Reports.Parts.Tables.DataTableHelpers.FilterByTagsMatch;        

        tagSetId?: number;
        tagSet?: InvData.Models.TagSet;        

        onStateChanged?: () => void;
        height?: number;
    }

    const NodeIdForTagNotSet = -9999999999;
    const NodeIdForTagSet = -9999999998;

    class TreeViewItem {        
        id: number;
        code: string;
        label: string;
        items: Array<TreeViewItem>;

        tag: InvData.Models.Tag;

        /**
         * The node for "Tag Set (Other)".
         */
        tagNotSetNode: TreeViewItem;
        tagOtherNode: TreeViewItem;

        selected: boolean = false;
        expanded: boolean = false;   

        static createForTagSet(tagSet: InvData.Models.TagSet, tagsMatch: Solovis.Reports.Parts.Tables.DataTableHelpers.FilterByTagsMatch) {
            let tagsMatchHelper = new TagFilterStateHelper(tagSet, tagsMatch);

            let rootNode = new TreeViewItem();
            rootNode.id = NodeIdForTagSet;
            rootNode.label = tagSet.label;
            rootNode.items = [];
            rootNode.expanded = true;

            rootNode.tagNotSetNode = TreeViewItem.createForTagNotSet(tagsMatchHelper);
            rootNode.items.push(rootNode.tagNotSetNode);

            let areAllRootTagsSelected = tagsMatchHelper.areAllRootTagsEnabled();
            if (areAllRootTagsSelected && rootNode.tagNotSetNode.selected) {
                rootNode.selected = true;
            }

            if (tagSet.rootTags) {
                for (let tag of tagSet.rootTags) {
                    let tagNode = TreeViewItem.createForTag(tag, tagsMatchHelper, areAllRootTagsSelected);                    

                    rootNode.items.push(tagNode);
                }
            }

            return rootNode;
        }

        static createForTagNotSet(tagsMatchHelper: TagFilterStateHelper): TreeViewItem {
            let tagNotSetNode = new TreeViewItem();
            tagNotSetNode.id = NodeIdForTagNotSet;
            tagNotSetNode.label = "(Not Set)";

            tagNotSetNode.selected = tagsMatchHelper.isTagNotSetEnabled();
            return tagNotSetNode;
        }

        static createForTag(tag: InvData.Models.Tag, tagsMatchHelper: TagFilterStateHelper, alwaysSelect?: boolean): TreeViewItem {
            let node = new TreeViewItem();
            node.id = tag.id;
            node.label = tag.label;
            node.tag = tag;            

            let entry = tagsMatchHelper.getEntryForTagId(tag.id);
            if (alwaysSelect) {
                node.selected = true;
            }
            else if (entry && !entry.excludeTag) {
                // Either a parent tag was selected or we specifically declared this tag enabled.
                // However, if the tag has child tags, we will select the Tag (Other) node below.
                if (_.isEmpty(tag.childTags) || (entry && entry.includeChildTags)) {
                    node.selected = true;
                }
            }    

            if (!_.isEmpty(tag.childTags)) {
                node.items = [];

                let nodeOther = new TreeViewItem();
                nodeOther.id = -tag.id;
                nodeOther.label = tag.label + " (Other)";
                if (entry && !entry.excludeTag) {
                    nodeOther.selected = true;
                }
                node.items.push(nodeOther);
                node.tagOtherNode = nodeOther;

                let alwaysSelectChildTags = alwaysSelect || (entry && entry.includeChildTags);

                for (let childTag of tag.childTags) {
                    let childTagNode = TreeViewItem.createForTag(childTag, tagsMatchHelper, alwaysSelectChildTags);
                    node.items.push(childTagNode);
                }
            }

            return node;
        }        
    
        getCurrentState() {
            if (this.id !== NodeIdForTagSet) {
                throw "getCurrentState should only be called on TreeView";
            }

            if (this.areAllChildLeafItemsSelected()) {
                // Everything is selected and we use null to represent the whole tag set being selected.
                // Throughout this class, we always rely on the state of the leaf nodes because we don't
                // want to rely on the current behavior of interior selected nodes.
                return null;
            }

            var state: Solovis.Reports.Parts.Tables.DataTableHelpers.FilterByTagsMatch = {};            
            state.notSet = this.tagNotSetNode.selected;

            if (this.areAllActualChildTagsSelected()) {
                state.includeAllRootTags = true;
            }
            else {
                let enabledTags: Solovis.Reports.Parts.Tables.DataTableHelpers.FilterByTagMatchEntry[] = [];
                this.collectTagStateForChildItems(enabledTags);

                state.enabledTags = enabledTags;
            }

            return state;
        }

        /**
         * Add all relevant state entries for this tag and child tags to enabledTags list.
         */
        collectTagState(enabledTags: Solovis.Reports.Parts.Tables.DataTableHelpers.FilterByTagMatchEntry[]) {
            if (!this.isTagItem()) {
                throw "collectTagState should only be called on tag item";
            }

            if (this.areAllChildLeafItemsSelected()) {
                // The node is selected, and we are at a leaf node, so we have definitely selected this.
                enabledTags.push({
                    tagId: this.id,
                    includeChildTags: true
                });
            }
            else if (!_.isEmpty(this.items)) {
                if (this.tagOtherNode.selected) {
                    // We know for a fact that at least one actual child tag was deselected (otherwise, this.selected would have been true too).
                    enabledTags.push({
                        tagId: this.id
                    });

                    // Also, check for the individual child tags.
                    this.collectTagStateForChildItems(enabledTags);
                }
                else {
                    if (this.areAllActualChildTagsSelected()) {
                        // Tag itself is not selected but all child tags are selected.
                        enabledTags.push({
                            tagId: this.id,
                            excludeTag: true,
                            includeChildTags: true
                        });
                    }
                    else {
                        // We did not include all child tags, so we proceed down the tree to check child state.
                        this.collectTagStateForChildItems(enabledTags);
                    }
                }
            }
        }

        collectTagStateForChildItems(enabledTags: Solovis.Reports.Parts.Tables.DataTableHelpers.FilterByTagMatchEntry[]) {
            for (let childTagItem of this.items) {
                if (childTagItem.isTagItem()) {
                    childTagItem.collectTagState(enabledTags);
                }
            }
        }

        isTagSetItem(): boolean {
            return this.id === NodeIdForTagSet;
        }

        isTagItem(): boolean {
            return !!this.tag;
        }

        areAllActualChildTagsSelected(): boolean {
            // We always only check leaf nodes because dxTreeView will play games with selected state
            // on interior nodes when filters are applied (i.e. node is selected if all visible child nodes 
            // are selected, as opposed to all existing child nodes - which is what we want).
            if (this.items) {
                for (let childItem of this.items) {
                    if (childItem.isTagItem()) {
                        if (!childItem.areAllChildLeafItemsSelected()) {
                            return false;
                        }
                    }
                }
            }            

            return true;
        }

        areAllChildLeafItemsSelected(): boolean {
            if (!_.isEmpty(this.items)) {
                for (let childItem of this.items) {
                    if (!childItem.areAllChildLeafItemsSelected()) {
                        return false;
                    }
                }

                return true;
            }
            else {
                // Leaf node so return its selected state.
                return this.selected;
            }            
        }
    }

    class TagFilterStateHelper {
        public tagSet: InvData.Models.TagSet;

        private _tagsMatch: Solovis.Reports.Parts.Tables.DataTableHelpers.FilterByTagsMatch;
        private _tagIdToEntryMap: { [tagId: number]: Solovis.Reports.Parts.Tables.DataTableHelpers.FilterByTagMatchEntry };

        constructor(tagSet: InvData.Models.TagSet, tagsMatch: Solovis.Reports.Parts.Tables.DataTableHelpers.FilterByTagsMatch) {
            this.tagSet = tagSet;

            this._tagsMatch = tagsMatch;

            this._tagIdToEntryMap = {};
            if (this._tagsMatch && this._tagsMatch.enabledTags) {
                for (let entry of this._tagsMatch.enabledTags) {
                    this._tagIdToEntryMap[entry.tagId] = entry;
                }
            }
        }        

        public areAllRootTagsEnabled(): boolean {
            if (!this._tagsMatch) {
                // Null state means everything is included.
                return true;
            }
            else {
                if (this._tagsMatch.includeAllRootTags) {
                    return true;
                }
                else {
                    if (!this.tagSet.rootTags) {
                        return false;
                    }
                    else {
                        let missingFound = false;
                        for (let tag of this.tagSet.rootTags) {
                            let entry = this.getEntryForTagId(tag.id);
                            if (!entry || entry.excludeTag) {
                                missingFound = true;
                            }
                            else if (!_.isEmpty(tag.childTags) && !entry.includeChildTags) {
                                missingFound = true;
                            }
                        }

                        return !missingFound;
                    }
                }
            }
        }

        public getEntryForTagId(tagId: number): Solovis.Reports.Parts.Tables.DataTableHelpers.FilterByTagMatchEntry {
            return this._tagIdToEntryMap[tagId];
        }

        public isTagNotSetEnabled(): boolean {
            if (!this._tagsMatch) {
                // Null state means everything is included.
                return true;
            }

            return !!this._tagsMatch.notSet;
        }
    }

    export class TagFilterControl extends Solovis.UI.BaseControl {        
        public _initialState: Solovis.Reports.Parts.Tables.DataTableHelpers.FilterByTagsMatch;      

        private _tagSetId;
        public _tagSet: InvData.Models.TagSet;

        public _treeViewContainer: JQuery;
        public _treeView: DevExpress.ui.dxTreeView;        
        public _initialHeight: number;

        private _tagSetTreeViewItem: TreeViewItem;
        
        private _onStateChangedHandler: () => void;

        constructor(options: TagFilterControlOptions) {
            super(options);

            this._initialState = options.initialState;

            this._tagSetId = options.tagSetId;
            this._tagSet = options.tagSet;    
            
            this._onStateChangedHandler = options.onStateChanged;
            this._initialHeight = options.height;
        }

        public async doInitAsync(): Promise<void> {
            if (!this._tagSet && this._tagSetId) {
                let sitePortalServices = new Solovis.Portal.SitePortalServices();
                this._tagSet = await sitePortalServices.modelEnvironmentCachedData().tryGetTagSetByIdAsync(this._tagSetId);
            }

            if (!this._tagSet) {
                throw "Tag set not available.";
            }

            this._tagSetId = this._tagSet.id;
        }

        public doRender() {
            var self = this;

            this._tagSetTreeViewItem = TreeViewItem.createForTagSet(this._tagSet, this._initialState);
            let treeViewItems = [this._tagSetTreeViewItem];                        

            this._treeViewContainer = $('<div>')                
                .appendTo(this.container);            

            this._treeView = $(this._treeViewContainer).dxTreeView({
                items: treeViewItems,
                dataStructure: 'tree',
                keyExpr: 'id',
                displayExpr: 'label',
                expandedExpr: 'expanded',
                showCheckBoxesMode: 'normal',                
                onItemClick: function (e: any) {                    
                    if (e.itemData.selected) {
                        // Toggle selection by item click as well
                        self._treeView.unselectItem(e.itemData);
                    } else {
                        self._treeView.selectItem(e.itemData);
                    }
                },
                onSelectionChanged: function () {
                    if (self._onStateChangedHandler) {
                        self._onStateChangedHandler();
                    }
                }
            }).dxTreeView('instance');  

            if (this._initialHeight) {
                this.setHeight(this._initialHeight);
            }         
        }

        public getCurrentState() {
            var currentState = this._tagSetTreeViewItem.getCurrentState();
            return currentState;
        }                  

        public setHeight(newHeight) {
            if (this._treeViewContainer) {
                $(this._treeViewContainer).height(newHeight);
            }
            else {
                this._initialHeight = newHeight;
            }
        }


        public static showAsDialog(
            options: {
                tagSetFilter: Solovis.Reports.Parts.Tables.DataTableHelpers.FilterByTagsMatch;
                tagSetId?: number;
                tagSet?: InvData.Models.TagSet;
            },
            newFilterCallback: (newFilter: Solovis.Reports.Parts.Tables.DataTableHelpers.FilterByTagsMatch) => any
        ) {
            var formDiv = $("<div>");

            var tagFilterControl = new TagFilterControl({
                controlHost: formDiv,
                tagSetId: options.tagSetId,
                tagSet: options.tagSet,
                height: 100,
                initialState: options.tagSetFilter                
            });
            tagFilterControl.setAsCurrentControl();

            var dialog = new Solovis.UI.Dialog({
                container: formDiv,                

                width: 600,
                height: 500,
                center: true,
                modal: true,
                title: "Tag Set Filter",

                noPaddingOnContent: true,

                buttons: [
                    {
                        label: "Clear",
                        click: () => {
                            newFilterCallback(null);
                            dialog.close();
                        }
                    },
                    {
                        label: "OK",
                        click: () => {
                            newFilterCallback(tagFilterControl.getCurrentState());
                            dialog.close();
                        }
                    },
                    {
                        label: "Cancel",
                        click: () => {
                            dialog.close();
                        }
                    }
                ],

                contentHeightSizer: {
                    setHeight: function (newHeight) {
                        tagFilterControl.setHeight(newHeight);
                    }
                }
            });
            dialog.open();
        }
    }
}
